/** Base URL for UrlScan.io public pages. */
export const URLSCAN_IO_BASE = 'https://urlscan.io';

/** Direct link to a UrlScan result page when the scan UUID is known. */
export function urlScanResultPageUrl(scanResultId) {
  if (scanResultId == null || scanResultId === '') return null;
  const id = String(scanResultId).trim();
  if (!id) return null;
  return `${URLSCAN_IO_BASE}/result/${id}/`;
}

/** Derive scan UUID from a UrlScan screenshot asset URL (legacy rows). */
export function extractUrlScanUuidFromScreenshotUrl(screenshotUrl) {
  if (!screenshotUrl) return null;
  const match = String(screenshotUrl).match(/\/screenshots\/([a-f0-9-]{36})\./i);
  return match ? match[1] : null;
}

/** Fallback UrlScan search URL when no scan UUID is stored. */
export function urlScanSearchUrlForValue(value, informationType) {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  if (!raw) return null;

  if (informationType === 'RELATED_DOMAIN' || informationType === 'LINK_DOMAINS'
      || (!/^https?:\/\//i.test(raw) && raw.includes('.'))) {
    const domain = raw.replace(/^https?:\/\//i, '').split('/')[0].split(':')[0].replace(/^\[|\]$/g, '');
    if (!domain) return null;
    return `${URLSCAN_IO_BASE}/search/#domain:${encodeURIComponent(domain)}`;
  }

  if (/^https?:\/\//i.test(raw)) {
    return `${URLSCAN_IO_BASE}/search/#page.url:"${escapeUrlScanSearchPhrase(raw)}"`;
  }

  return `${URLSCAN_IO_BASE}/search/#domain:${encodeURIComponent(raw)}`;
}

function escapeUrlScanSearchPhrase(value) {
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * Normalize investigation target to the URL submitted for a direct UrlScan scan (matches backend).
 * Bare domains become http://host (not https).
 */
export function normalizeInvestigationScanTargetUrl(investigationTarget) {
  if (investigationTarget == null || investigationTarget === '') return null;
  let raw = String(investigationTarget).trim();
  if (!raw) return null;
  if (!/^https?:\/\//i.test(raw)) {
    if (!raw.includes(' ') && raw.includes('.')) {
      raw = `http://${raw}`;
    }
  }
  return raw;
}

/** UrlScan search UI for scans of one exact page URL (direct scan), not domain-wide search. */
export function urlScanPageUrlSearchUrl(pageUrl) {
  if (pageUrl == null || pageUrl === '') return null;
  const raw = String(pageUrl).trim();
  if (!raw) return null;
  return `${URLSCAN_IO_BASE}/search/#page.url:"${escapeUrlScanSearchPhrase(raw)}"`;
}

/** UrlScan homepage — submit a new live scan when no result UUID is stored. */
export function urlScanSubmitHomeUrl() {
  return `${URLSCAN_IO_BASE}/`;
}

function normalizeScanTargetKey(value) {
  if (value == null) return '';
  return String(value).trim().toLowerCase().replace(/\/$/, '');
}

/** True when a stored scan row refers to the same URL that was submitted for direct scan. */
export function scanRowMatchesInvestigationTarget(rowValue, investigationTarget) {
  const a = normalizeScanTargetKey(rowValue);
  const b = normalizeScanTargetKey(investigationTarget);
  if (!a || !b) return false;
  if (a === b) return true;
  const stripScheme = (s) => s.replace(/^https?:\/\//i, '');
  return stripScheme(a) === stripScheme(b);
}

/**
 * UrlScan.io link for the direct scan of an investigation target (result page when UUID is known).
 * @param {string} investigationTarget
 * @param {Array<{ informationType?: string, informationValue?: string, scanResultId?: string, screenshotUrl?: string }>} scanBlockRows
 */
export function urlScanInvestigationScanUrl(investigationTarget, scanBlockRows) {
  if (investigationTarget == null || investigationTarget === '') return null;
  const rows = Array.isArray(scanBlockRows) ? scanBlockRows : [];

  const resultIdFromRow = (r) => {
    if (!r) return null;
    return urlScanResultPageUrl(r.scanResultId)
      || urlScanResultPageUrl(extractUrlScanUuidFromScreenshotUrl(r.screenshotUrl));
  };

  const screenshot = rows.find(
    (r) => r.informationType === 'SCREENSHOT'
      && scanRowMatchesInvestigationTarget(r.informationValue, investigationTarget),
  );
  let resultUrl = resultIdFromRow(screenshot);
  if (!resultUrl) {
    const anyScreenshot = rows.find((r) => r.informationType === 'SCREENSHOT');
    resultUrl = resultIdFromRow(anyScreenshot);
  }
  if (!resultUrl) {
    const withId = rows.find(
      (r) => r.scanResultId && (r.informationType === 'SCREENSHOT' || r.informationType === 'LINKS'),
    );
    resultUrl = resultIdFromRow(withId);
  }
  if (resultUrl) return resultUrl;

  const scanUrl = normalizeInvestigationScanTargetUrl(investigationTarget);
  if (scanUrl && /^https?:\/\//i.test(scanUrl)) {
    return urlScanPageUrlSearchUrl(scanUrl);
  }
  return urlScanSubmitHomeUrl();
}

/**
 * UrlScan.io search link for historical search of an investigation target (domain / page field).
 */
export function urlScanInvestigationSearchUrl(investigationTarget) {
  if (investigationTarget == null || investigationTarget === '') return null;
  // Match investigation UrlScan Search (domain: field, includes subdomains).
  return urlScanSearchUrlForValue(investigationTarget, 'RELATED_DOMAIN');
}

/**
 * Best UrlScan link for a row: result page when UUID is known, otherwise search.
 */
export function resolveUrlScanExternalUrl(info) {
  if (!info) return null;

  // Hostnames extracted from another scan: open UrlScan search for that domain.
  if (info.informationType === 'RELATED_DOMAIN' || info.informationType === 'LINK_DOMAINS') {
    return urlScanSearchUrlForValue(info.informationValue, info.informationType);
  }

  const fromStoredId = urlScanResultPageUrl(info.scanResultId);
  if (fromStoredId) return fromStoredId;

  const fromScreenshot = urlScanResultPageUrl(
    extractUrlScanUuidFromScreenshotUrl(info.screenshotUrl),
  );
  if (fromScreenshot) return fromScreenshot;

  return urlScanSearchUrlForValue(info.informationValue, info.informationType);
}
